import React, { useState, useContext, useEffect } from "react";
import styles from "../styles/TokensPage.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTrash,
  faPlus,
  faKey,
  faExclamationTriangle,
  faTimes,
  faCopy,
} from "@fortawesome/free-solid-svg-icons";
import { AuthContext } from "../AuthProvider";
import { getTokens, createToken, deleteToken } from "../api/api";
import { useSnackbar } from "notistack";

const Tokens = () => {
  const { keycloak } = useContext(AuthContext);
  const [tokens, setTokens] = useState([]);
  const [newToken, setNewToken] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const getValidAccessToken = async () => {
    try {
      // Ensure the token is still valid (refresh if needed)
      if (keycloak.isTokenExpired(30)) {
        // Refresh if expires in next 30 seconds
        await keycloak.updateToken(30);
      }
      return keycloak.token;
    } catch (error) {
      console.error("Failed to refresh token", error);
      return null;
    }
  };

  const fetchTokens = async () => {
    try {
      const token = await getValidAccessToken();
      if (!token) return;
      const data = await getTokens(token);
      setTokens(data);
    } catch (error) {
      console.error("Error fetching tokens:", error);
    }
  };

  useEffect(() => {
    fetchTokens();
  }, []);

  // Handle modal close
  const closeModal = () => {
    if (newToken) {
      fetchTokens();
    }
    setNewToken(null);
    setShowModal(false);
    enqueueSnackbar("New token created", {
      variant: "success",
    });
  };

  // Generate a new token (simulated)
  const generateToken = async () => {
    setIsSaving(true);
    const token = await getValidAccessToken();
    if (!token) return;
    try {
      const newToken = await createToken(token);
      setNewToken(newToken); // Store the token temporarily
      setShowModal(true);
    } catch (error) {
      enqueueSnackbar("Failed to generate token", {
        variant: "error",
      });
      console.error("Error generating token:", error);
    }
    setIsSaving(false);
  };

  // Delete token
  const deleteToke = async (id) => {
    setIsSaving(true);
    const token = await getValidAccessToken();
    if (!token) return;
    try {
      const tokenDeleted = await deleteToken(token, id);
      if (tokenDeleted) {
        enqueueSnackbar("Token deleted", {
          variant: "success",
        });
        setTokens(tokens.filter((token) => token.id !== id));
      } else {
        enqueueSnackbar("Failed to delete token", {
          variant: "error",
        });
      }
    } catch (error) {
      enqueueSnackbar(`Failed to delete token`, {
        variant: "error",
      });
      console.error("Error deleting token:", error);
    }
    setIsSaving(false);
  };

  // Add a function to copy token to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(newToken).then(
      () => {
        enqueueSnackbar("Token copied to clipboard", {
          variant: "success",
        });
      },
      (err) => {
        console.error("Failed to copy token: ", err);
        enqueueSnackbar("Failed to copy token", {
          variant: "error",
        });
      }
    );
  };

  return (
    <div className={styles.tokenContainer}>
      <div className={styles.header}>
        <h2>
          <FontAwesomeIcon icon={faKey} className={styles.icon} /> Hub API Tokens
        </h2>
        <button
          className={styles.createButton}
          onClick={generateToken}
          disabled={isSaving}
        >
          <FontAwesomeIcon icon={faPlus} /> Create Token
        </button>
      </div>

      {tokens.length > 0 ? (
        <div className={styles.tokenList}>
          {tokens.map((token) => (
            <div key={token.id} className={styles.tokenCard}>
              <div className={styles.tokenInfo}>
                <span className={styles.tokenName}>{token.id}</span>
                <span className={styles.tokenValue}>{token.created_at}</span>
              </div>
              <button
                className={styles.deleteButton}
                disabled={isSaving}
                onClick={() => deleteToke(token.id)}
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className={styles.noTokens}>No Hub API tokens available.</p>
      )}

      {/* Token Modal */}
      {showModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <h3>Hub API Token</h3>
            <p className={styles.warning}>
              <FontAwesomeIcon icon={faExclamationTriangle} />
              Copy this token now! You won't be able to see it again.
            </p>
            <div className={styles.tokenContainer}>
              <div className={styles.tokenDisplay}>
                <span className={styles.tokenText}>{newToken}</span>
                <button
                  className={styles.copyButton}
                  onClick={copyToClipboard}
                  title="Copy to clipboard"
                >
                  <FontAwesomeIcon icon={faCopy} />
                </button>
              </div>
            </div>
            <button className={styles.closeButton} onClick={closeModal}>
              <FontAwesomeIcon icon={faTimes} /> Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tokens;
